import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, getDoc, collection, query, where, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import { useFirebase } from '../contexts/FirebaseContext';
import {
  Clock, Phone, User, Building2, ChevronLeft, Check
} from 'lucide-react';
import { formatCurrencyXCD } from '../config/JadConfig';

const loadLeaflet = () => {
  return new Promise((resolve, reject) => {
    if (window.L) {
      resolve(window.L);
      return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => resolve(window.L);
    script.onerror = () => reject(new Error('Failed to load Leaflet'));
    document.head.appendChild(script);
  });
};

// Statuses may be stored 'Pending', 'In Delivery', 'inDelivery', 'picked_up'…
// — normalize before comparing so live tracking never breaks.
const normalizeStatus = (s) => (s || '').toString().toLowerCase().replace(/[\s_-]/g, '');

// iOS SharedComponents OrderStatusBadge colors (system colors).
const BADGE_COLORS = {
  pending:    '#FF9500', // .orange
  confirmed:  '#007AFF', // .blue
  preparing:  '#FFCC00', // .yellow
  ready:      '#AF52DE', // .purple
  onmyway:    '#007AFF', // .blue
  arrived:    '#AF52DE', // .purple
  indelivery: '#5856D6', // .indigo
  delivered:  '#34C759', // .green
  completed:  '#34C759', // .green
  cancelled:  '#FF3B30', // .red
  pickedup:   '#34C759', // .green
};

// iOS OrderStatus.orderDisplayName labels.
const STATUS_LABELS = {
  pending:    'Placed',
  confirmed:  'Confirmed',
  preparing:  'Preparing',
  ready:      'Ready',
  onmyway:    'On Way',
  arrived:    'Arrived',
  indelivery: 'Delivering',
  delivered:  'Delivered',
  completed:  'Complete',
  cancelled:  'Cancelled',
  pickedup:   'Picked Up',
};

// iOS OrderProgressView progressSteps — same 9 steps for every order type.
const PROGRESS_STEPS = [
  'pending', 'confirmed', 'preparing', 'ready', 'pickedup',
  'onmyway', 'arrived', 'indelivery', 'delivered'
];

function OrderTrackingPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { db } = useFirebase();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [driverPhone, setDriverPhone] = useState(null);
  const [driverProfile, setDriverProfile] = useState(null);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const driverListenerRef = useRef(null);

  // Load order — real-time listener mirrors iOS startOrderListening()
  useEffect(() => {
    if (!orderId) {
      setError('Order ID is required');
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'orders', orderId),
      async (docSnap) => {
        if (!docSnap.exists()) {
          setError('Order not found');
          setLoading(false);
          return;
        }

        const data = docSnap.data();
        const orderWithDate = {
          ...data,
          id: docSnap.id,
          createdAt: data.createdAt?.toDate() || new Date()
        };
        setOrder(orderWithDate);
        setLoading(false);

        // Fetch driver phone/profile once
        if (orderWithDate.driverId) {
          fetchDriverInfo(orderWithDate.driverId);
        }
      },
      (err) => {
        setError(err.message || 'Failed to load order');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [orderId, db]);

  // Subscribe to driver location — mirrors iOS startDriverTracking()
  useEffect(() => {
    if (!order?.driverId) return;

    const unsubscribe = onSnapshot(
      doc(db, 'drivers', order.driverId, 'locations', 'current'),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.latitude && data.longitude && (data.latitude !== 0 || data.longitude !== 0)) {
            setDriverLocation({ lat: data.latitude, lng: data.longitude });
          }
        }
      },
      (err) => {
        console.error('Driver location error:', err);
      }
    );

    driverListenerRef.current = unsubscribe;
    return () => unsubscribe();
  }, [order?.driverId, db]);

  // Mirrors iOS ProximityNotificationManager: while the order is in delivery,
  // watch the driver's live location and fire one "driver_nearby" notification
  // when the driver gets within half a mile (804.672m) of the delivery address.
  const nearbyNotifiedRef = useRef(false);
  useEffect(() => {
    if (!driverLocation || !order || nearbyNotifiedRef.current) return;
    const EN_ROUTE = new Set(['in delivery', 'on my way', 'picked up']);
    if (!EN_ROUTE.has((order.status || '').toLowerCase())) return;
    const coords = order.deliveryAddress?.coordinates;
    if (!coords?.latitude || !coords?.longitude) return;

    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(driverLocation.lat - coords.latitude);
    const dLng = toRad(driverLocation.lng - coords.longitude);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(coords.latitude)) *
        Math.cos(toRad(driverLocation.lat)) *
        Math.sin(dLng / 2) ** 2;
    const meters = 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    if (meters > 804.672) return;

    nearbyNotifiedRef.current = true;
    (async () => {
      // iOS dedupes by checking for a driver_nearby notification on this
      // order within the last 30 minutes.
      const snap = await getDocs(
        query(
          collection(db, 'notifications'),
          where('userId', '==', order.customerId),
          where('orderId', '==', order.id),
          where('type', '==', 'driver_nearby')
        )
      );
      const cutoff = Date.now() - 30 * 60 * 1000;
      const recent = snap.docs.some(
        (d) => (d.data().createdAt?.toMillis?.() ?? 0) > cutoff
      );
      if (recent) return;
      await addDoc(collection(db, 'notifications'), {
        userId: order.customerId,
        type: 'driver_nearby',
        title: 'Driver Nearby',
        message: `${order.driverName || 'Your driver'} is nearby and will arrive soon!`,
        orderId: order.id,
        restaurantId: order.restaurantId ?? null,
        driverId: order.driverId ?? null,
        isRead: false,
        createdAt: Timestamp.now(),
      });
    })().catch((e) => console.warn('driver_nearby check failed:', e));
  }, [driverLocation, order, db]);

  const fetchDriverInfo = async (driverId) => {
    try {
      const profileSnap = await getDoc(doc(db, 'users', driverId));
      if (profileSnap.exists()) {
        const profile = profileSnap.data();
        setDriverProfile(profile);
        setDriverPhone(profile.phone || null);
        return;
      }

      const driverSnap = await getDoc(doc(db, 'drivers', driverId));
      if (driverSnap.exists()) {
        const data = driverSnap.data();
        setDriverPhone(data.phoneNumber || null);
      }
    } catch (err) {
      console.error('Error fetching driver info:', err);
    }
  };

  const handleCallDriver = () => {
    if (driverPhone) {
      window.location.href = `tel:${driverPhone}`;
    }
  };

  // Initialize / update map — Leaflet equivalent of the iOS MKMapView section
  useEffect(() => {
    if (!order || !mapRef.current) return;
    const restaurantCoords = order?.restaurantLocation?.coordinates;
    const deliveryCoords = order?.deliveryAddress?.coordinates;

    if (!restaurantCoords?.latitude || !restaurantCoords?.longitude) return;

    let cleanup = null;

    loadLeaflet()
      .then((L) => {
        if (mapInstanceRef.current) {
          updateMapMarkers(L);
          return;
        }

        const map = L.map(mapRef.current);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors'
        }).addTo(map);

        mapInstanceRef.current = map;
        updateMapMarkers(L);
        cleanup = () => map.remove();
      })
      .catch((err) => console.error('Map load failed:', err));

    return () => { if (cleanup) cleanup(); };
  }, [order, driverLocation?.lat, driverLocation?.lng]);

  const updateMapMarkers = (L) => {
    const map = mapInstanceRef.current;
    if (!map || !L) return;

    const restaurantCoords = order?.restaurantLocation?.coordinates;
    const deliveryCoords = order?.deliveryAddress?.coordinates;

    if (!restaurantCoords?.latitude || !restaurantCoords?.longitude) return;

    // Clear old markers
    Object.values(markersRef.current).forEach((m) => {
      if (map.hasLayer(m)) map.removeLayer(m);
    });
    markersRef.current = {};

    const points = [];

    // iOS: building.2.fill, .red
    const restaurantIcon = L.divIcon({
      className: 'custom-marker',
      html: `<div style="width:32px;height:32px;border-radius:50%;background:#FF3B30;color:#fff;display:flex;align-items:center;justify-content:center;font-size:14px;box-shadow:0 2px 6px rgba(0,0,0,0.3);">🏪</div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 32]
    });
    const rMarker = L.marker([restaurantCoords.latitude, restaurantCoords.longitude], { icon: restaurantIcon })
      .addTo(map)
      .bindPopup(order.restaurantLocation?.name || order.restaurantName || 'Restaurant');
    markersRef.current.restaurant = rMarker;
    points.push([restaurantCoords.latitude, restaurantCoords.longitude]);

    // iOS: house.fill, .blue
    if (deliveryCoords?.latitude && deliveryCoords?.longitude) {
      const deliveryIcon = L.divIcon({
        className: 'custom-marker',
        html: `<div style="width:32px;height:32px;border-radius:50%;background:#007AFF;color:#fff;display:flex;align-items:center;justify-content:center;font-size:14px;box-shadow:0 2px 6px rgba(0,0,0,0.3);">🏠</div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 32]
      });
      const dMarker = L.marker([deliveryCoords.latitude, deliveryCoords.longitude], { icon: deliveryIcon })
        .addTo(map)
        .bindPopup(order.deliveryAddress?.name || 'Delivery');
      markersRef.current.delivery = dMarker;
      points.push([deliveryCoords.latitude, deliveryCoords.longitude]);
    }

    // iOS: car.fill, .green
    if (driverLocation) {
      const driverIcon = L.divIcon({
        className: 'custom-marker',
        html: `<div style="width:32px;height:32px;border-radius:50%;background:#34C759;color:#fff;display:flex;align-items:center;justify-content:center;font-size:14px;box-shadow:0 2px 6px rgba(0,0,0,0.3);">🚗</div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 32]
      });
      const drMarker = L.marker([driverLocation.lat, driverLocation.lng], { icon: driverIcon })
        .addTo(map)
        .bindPopup('Driver');
      markersRef.current.driver = drMarker;
      points.push([driverLocation.lat, driverLocation.lng]);
    }

    const bounds = L.latLngBounds(points.map(([lat, lng]) => L.latLng(lat, lng)));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F2F2F7]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B30]"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[#F2F2F7]">
        <div className="text-center">
          <p className="text-[#FF3B30] mb-4">{error || 'Order not found'}</p>
          <button
            onClick={() => navigate('/orders')}
            className="px-4 py-2 bg-[#FF3B30] text-white font-semibold rounded-xl"
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  const statusKey = normalizeStatus(order.status);
  const badgeColor = BADGE_COLORS[statusKey] || '#8E8E93';
  const badgeLabel = STATUS_LABELS[statusKey] || order.status;
  const currentStepIndex = Math.max(0, PROGRESS_STEPS.indexOf(statusKey));
  const inDeliveryPhase = ['onmyway', 'arrived', 'indelivery'].includes(statusKey);
  const showEta = inDeliveryPhase || !!order.estimatedDeliveryTime;

  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      {/* Header — mirrors iOS title row + OrderStatusBadge (back chevron for web nav) */}
      <header className="sticky top-0 z-40 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="flex items-center gap-2 px-2 py-3 max-w-lg mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 -ml-1 text-[#007AFF] shrink-0"
            aria-label="Back"
          >
            <ChevronLeft size={26} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold text-gray-900">Order #{order.id.slice(0, 8)}</h1>
            <p className="text-sm text-[#8E8E93]">Tracking your order</p>
          </div>
          <span
            className="px-2 py-1 rounded-lg text-xs font-medium shrink-0"
            style={{ backgroundColor: `${badgeColor}33`, color: badgeColor }}
          >
            {badgeLabel}
          </span>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4 pb-10">
        {/* Estimated Delivery — iOS etaCard */}
        {showEta && (
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <Clock size={20} className="text-[#FF9500] shrink-0" />
              <div>
                <h3 className="text-base font-semibold text-gray-900">Estimated Delivery</h3>
                <p className="text-sm text-[#8E8E93]">
                  {order.estimatedDeliveryTime
                    ? new Date(order.estimatedDeliveryTime?.toDate ? order.estimatedDeliveryTime.toDate() : order.estimatedDeliveryTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : 'Calculating...'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Map — iOS 300pt rounded map, Leaflet on web */}
        <div className="relative rounded-xl overflow-hidden h-[300px] bg-black/5 shadow-sm">
          <div ref={mapRef} className="w-full h-full" />
        </div>

        {/* Order Progress — iOS OrderProgressView: 20pt circles + 2pt connectors */}
        <div className="bg-white rounded-xl p-4 shadow-sm space-y-4">
          <h3 className="text-base font-semibold text-gray-900">Order Progress</h3>

          <div className="flex items-center">
            {PROGRESS_STEPS.map((step, index) => (
              <React.Fragment key={step}>
                <div
                  className="w-5 h-5 rounded-full shrink-0 flex items-center justify-center"
                  style={{ backgroundColor: index <= currentStepIndex ? '#34C759' : 'rgba(60,60,67,0.3)' }}
                >
                  {index < currentStepIndex ? (
                    <Check size={12} className="text-white" strokeWidth={3} />
                  ) : index === currentStepIndex ? (
                    <div className="w-2 h-2 rounded-full bg-white" />
                  ) : null}
                </div>
                {index < PROGRESS_STEPS.length - 1 && (
                  <div
                    className="flex-1 h-0.5"
                    style={{ backgroundColor: index < currentStepIndex ? '#34C759' : 'rgba(60,60,67,0.3)' }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="flex">
            {PROGRESS_STEPS.map((step) => (
              <span key={step} className="flex-1 text-[10px] text-[#8E8E93] text-center leading-tight">
                {STATUS_LABELS[step]}
              </span>
            ))}
          </div>
        </div>

        {/* Restaurant Info — iOS building.2 red card */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <Building2 size={20} className="text-[#FF3B30] shrink-0" />
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                {order.restaurantLocation?.name || order.restaurantName || 'Restaurant'}
              </h3>
              <p className="text-sm text-[#8E8E93]">Preparing your order</p>
            </div>
          </div>
        </div>

        {/* Driver Info — iOS shows whenever a driver is assigned */}
        {order.driverName && (
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              {driverProfile?.profileImageURL ? (
                <img
                  src={driverProfile.profileImageURL}
                  alt="Driver"
                  className="w-[50px] h-[50px] rounded-full object-cover ring-2 ring-[#34C759] shrink-0"
                />
              ) : (
                <div className="w-[50px] h-[50px] rounded-full bg-[#34C759]/10 flex items-center justify-center shrink-0">
                  <User size={22} className="text-[#34C759]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-gray-900">{order.driverName}</h3>
                <p className="text-sm text-[#8E8E93]">Your delivery driver</p>
              </div>
              <button
                onClick={handleCallDriver}
                disabled={!driverPhone}
                className={`w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0 ${
                  driverPhone ? 'bg-[#34C759]' : 'bg-[#8E8E93]'
                }`}
                aria-label="Call driver"
              >
                <Phone size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Order Items — iOS card with summary breakdown */}
        <div className="bg-white rounded-xl p-4 shadow-sm space-y-3">
          <h3 className="text-base font-semibold text-gray-900">Order Items</h3>

          <div className="space-y-3">
            {(order.items || order.orderItems || []).map((item, index) => (
              <div key={item.id || item.menuItemId || index} className="flex items-center text-sm">
                <span className="text-[#8E8E93] w-8 shrink-0">{item.quantity || 1}x</span>
                <span className="flex-1 text-gray-900">{item.menuItemName || item.name}{item.variantName ? ` (Size: ${item.variantName})` : ''}</span>
                <span className="font-semibold">{formatCurrencyXCD((item.price || 0) * (item.quantity || 1))}</span>
              </div>
            ))}
          </div>

          <div className="border-t border-black/[0.08] pt-3 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-900">Subtotal</span>
              <span className="font-semibold">{formatCurrencyXCD(order.subtotal ?? order.totalAmount ?? 0)}</span>
            </div>
            {(order.orderType || '').toString().toLowerCase() === 'delivery' && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-900">Delivery Fee</span>
                <span className="font-semibold">{formatCurrencyXCD(order.deliveryFee || 0)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-gray-900">Chef Service Fee (2%)</span>
              <span className="font-semibold">{formatCurrencyXCD(order.chefServiceFee || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-semibold text-[#34C759]">Total</span>
              <span className="font-semibold text-[#34C759]">{formatCurrencyXCD(order.totalAmount || 0)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderTrackingPage;
